import {
  ArrowsClockwiseIcon,
  ImageBrokenIcon,
  ImageSquareIcon,
  MagnifyingGlassPlusIcon,
  UploadSimpleIcon,
  XIcon,
} from '@phosphor-icons/react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { ImagePreviewModal } from '@/components/ui/image-preview-modal'
import { Typography } from '@/components/ui/typography'
import { popupFactory } from '@/factories'

import * as S from './file-upload.styled'
import { FileUploadPreviewShape, FileUploadSize } from './file-upload.types'
import { parseLabelParts } from './file-upload.utils'

interface MaximizedUploadProps {
  files: Array<File | string>
  disabled: boolean
  multiple: boolean
  size: FileUploadSize
  previewShape?: FileUploadPreviewShape
  zoomable?: boolean
  label?: string
  required?: boolean
  description?: string
  error?: string
  existingImageUrl?: string
  acceptedFilesText?: string
  uploadLabel: string
  onUploadClick: () => void
  onRemoveFile: () => void
}

export const MaximizedUpload = ({
  files,
  disabled,
  multiple,
  size,
  previewShape,
  zoomable = true,
  label,
  required,
  description,
  error,
  existingImageUrl,
  acceptedFilesText,
  uploadLabel,
  onUploadClick,
  onRemoveFile,
}: MaximizedUploadProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null)
  const visibleExistingUrl = existingImageUrl !== brokenUrl ? existingImageUrl : undefined

  useEffect(() => {
    const file = files[0]

    if (!file) {
      setPreviewUrl(null)
      return
    }

    if (typeof file === 'string') {
      setPreviewUrl(file)
      return
    }

    const url = URL.createObjectURL(file)
    setPreviewUrl(url)

    return () => URL.revokeObjectURL(url)
  }, [files])

  const [labelMain, labelBracket] = parseLabelParts(label)

  const zoomSrc = zoomable ? (previewUrl ?? visibleExistingUrl) : undefined
  const openPreview = () => {
    if (!zoomSrc) return

    popupFactory.open(
      ImagePreviewModal,
      { images: [zoomSrc], title: labelMain ?? 'Preview' },
      { suspendCurrent: true },
    )
  }

  return (
    <S.MaximizedContainer>
      {(labelMain || description) && (
        <S.MaximizedTextGroup>
          {labelMain && (
            <Typography size="heading-small" color="black800">
              {labelMain}
              {labelBracket && <S.LabelBracket> {labelBracket}</S.LabelBracket>}
              {required && <span aria-hidden="true"> *</span>}
            </Typography>
          )}
          {description && (
            <Typography size="text-large" color="black500">
              {description}
            </Typography>
          )}
        </S.MaximizedTextGroup>
      )}

      <S.PreviewRow>
        <S.PreviewBox
          $size={size}
          $shape={previewShape}
          $zoomable={Boolean(zoomSrc)}
          role={zoomSrc ? 'button' : undefined}
          tabIndex={zoomSrc ? 0 : undefined}
          aria-label={zoomSrc ? 'Zoom image' : undefined}
          onClick={openPreview}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              openPreview()
            }
          }}
        >
          {previewUrl ? (
            <S.PreviewImg
              src={previewUrl}
              alt={
                files[0]
                  ? typeof files[0] === 'string'
                    ? files[0].split('/').pop()
                    : files[0].name
                  : undefined
              }
            />
          ) : visibleExistingUrl ? (
            <S.PreviewImg
              src={visibleExistingUrl}
              alt=""
              onError={() => setBrokenUrl(visibleExistingUrl)}
            />
          ) : (
            <S.PreviewPlaceholder>
              <Icon
                icon={
                  existingImageUrl && brokenUrl === existingImageUrl
                    ? ImageBrokenIcon
                    : ImageSquareIcon
                }
                size="4.8rem"
                color="black300"
              />
            </S.PreviewPlaceholder>
          )}
          {zoomSrc && (
            <S.PreviewZoomHint>
              <Icon icon={MagnifyingGlassPlusIcon} size="3.2rem" color="white" />
            </S.PreviewZoomHint>
          )}
        </S.PreviewBox>

        <S.PreviewActions>
          {(files[0] ?? existingImageUrl) && (
            <S.MaximizedFileRow>
              <S.MaximizedFileName
                forwardedAs="span"
                size="text-medium"
                color="black800"
                weight="semibold"
                breakLines={1}
              >
                {typeof files[0] === 'string'
                  ? files[0].split('/').pop()
                  : (files[0]?.name ?? 'Current photo')}
              </S.MaximizedFileName>
              <Button onlyBox use="ghost" size="small" onClick={onRemoveFile}>
                <Icon icon={XIcon} />
              </Button>
            </S.MaximizedFileRow>
          )}
          {error && (
            <S.MaximizedErrorRow role="alert">
              <Typography size="heading-tiny" color="red500" textAlign="center">
                {error}
              </Typography>
            </S.MaximizedErrorRow>
          )}
          <S.FullWidthButton
            use="primary"
            size="medium"
            disabled={disabled}
            onClick={onUploadClick}
          >
            <Icon icon={!multiple && files.length > 0 ? ArrowsClockwiseIcon : UploadSimpleIcon} />
            {!multiple && files.length > 0 ? 'Replace' : uploadLabel}
          </S.FullWidthButton>

          {acceptedFilesText && (
            <S.AcceptedFilesText size="text-medium" color="black500" textAlign="center">
              {acceptedFilesText}
            </S.AcceptedFilesText>
          )}
        </S.PreviewActions>
      </S.PreviewRow>
    </S.MaximizedContainer>
  )
}
